import { QueryClient } from '@tanstack/react-query';

import {
  getWebSocketPayloadArray,
  parseEventPayload,
} from '@app/helpers/websocket-event-payload';

import * as constants from '../constants/websocket';

interface MessageEvent {
  detail?: {
    payload?: Array<{
      name: string;
      payload: unknown;
      userId?: string;
    }>;
  };
}

interface NewMessageEventData {
  chatId?: string;
  additionalInfo?: {
    eventType?: string;
    agentId?: string;
  };
}

const AGENT_EVENT_TYPES = new Set([
  'agent_started',
  'agent_completed',
  'agent_escalated',
  'agent_stopped',
]);

/**
 * Handles new message events by invalidating the chatAgent query.
 * Only invalidates when the message is an agent-related event
 * (started/completed/escalated/stopped).
 */
const handleNewMessageEvent = (data: unknown, queryClient: QueryClient) => {
  const messageData = data as NewMessageEventData;
  const { chatId, additionalInfo } = messageData;

  // Only invalidate if this is an agent-related event
  if (
    chatId &&
    additionalInfo?.eventType &&
    AGENT_EVENT_TYPES.has(additionalInfo.eventType)
  ) {
    queryClient.invalidateQueries({ queryKey: ['chatAgent', chatId] });
  }
};

const agentEventMap: Record<
  string,
  (data: unknown, queryClient: QueryClient) => void
> = {};

agentEventMap[constants.NEW_MESSAGE] = handleNewMessageEvent;

export const AGENT_RELATED_EVENTS = new Set([constants.NEW_MESSAGE]);

/**
 * Event processor for agent-related React Query cache invalidations.
 * Follows the same pattern as job-event-processor.ts to keep concerns separated.
 */
export const agentEventProcessor =
  (queryClient: QueryClient) => (messageEvent: MessageEvent) => {
    const payload = getWebSocketPayloadArray(messageEvent);
    if (!payload) return;

    const agentEvents = payload.filter((event) =>
      AGENT_RELATED_EVENTS.has(event.name),
    );

    agentEvents.forEach((event) => {
      const eventHandler = agentEventMap[event.name];
      if (eventHandler) {
        try {
          const data = parseEventPayload(event.payload);
          eventHandler(data, queryClient);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('agent-event-processor error:', event.payload, err);
        }
      }
    });
  };
